import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Insignia, Tarjeta } from '../componentes/ui/basicos';
import { Dialogo } from '../componentes/ui/dialogo';
import { Campo, Interruptor, Selector } from '../componentes/ui/formulario';
import { Cargando, Problema, Vacio, useTextoDeError } from '../componentes/ui/estados';
import { Tabla, type Columna } from '../componentes/ui/tabla';
import { usePermiso, useSesion } from '../sesion/ProveedorDeSesion';

interface Membresia {
  readonly id: string;
  readonly usuarioId: string;
  readonly correo: string;
  readonly nombre: string;
  readonly apellido: string | null;
  readonly rolId: string;
  readonly rol: string;
  readonly activa: boolean;
  readonly sucursalesPermitidas: string[] | null;
  readonly sucursalPredeterminadaId: string | null;
}

interface Sucursal {
  readonly id: string;
  readonly nombre: string;
  readonly activa: boolean;
}

interface Rol {
  readonly id: string;
  readonly codigo: string;
  readonly nombre: string;
  readonly esDeSistema: boolean;
  readonly permisos: string[];
  readonly personas: number;
}

interface Permiso {
  readonly codigo: string;
  readonly modulo: string;
  readonly claveDeTraduccion: string;
}

/**
 * Quién forma parte del comercio y qué puede hacer.
 *
 * La invitación no manda a nadie a otra pantalla: al emitirla se muestra el enlace, para
 * poder pasarlo por donde sea —correo, mensaje, en persona— sin depender de que el correo
 * haya salido.
 */
export function Gente(): React.ReactElement {
  const { t } = useTranslation();
  const clienteDeConsultas = useQueryClient();
  const textoDeError = useTextoDeError();
  const { perfil } = useSesion();

  const puedeInvitar = usePermiso('ACCESOS.MEMBRESIA.INVITAR');
  const puedeEditarGente = usePermiso('ACCESOS.MEMBRESIA.EDITAR');
  const puedeVerRoles = usePermiso('ACCESOS.ROL.VER');
  const puedeCrearRoles = usePermiso('ACCESOS.ROL.CREAR');
  const puedeAdministrarTipos = usePermiso('VENTAS.TIPO_DE_COMPROBANTE.ADMINISTRAR');
  const puedeEditarRoles = usePermiso('ACCESOS.ROL.EDITAR');
  const puedeVerSucursales = usePermiso('EMPRESA.SUCURSAL.VER');

  const [invitando, setInvitando] = useState(false);
  const [correo, setCorreo] = useState('');
  const [rolElegido, setRolElegido] = useState('');
  const [enlace, setEnlace] = useState<string | null>(null);
  const [errorDeInvitacion, setErrorDeInvitacion] = useState<string | null>(null);

  const [corrigiendo, setCorrigiendo] = useState<Membresia | null>(null);
  const [corrigiendoRol, setCorrigiendoRol] = useState<Rol | null>(null);

  const [creandoRol, setCreandoRol] = useState(false);
  const [rolNuevo, setRolNuevo] = useState({ codigo: '', nombre: '' });
  const [permisosElegidos, setPermisosElegidos] = useState<Set<string>>(new Set());
  const [errorDeRol, setErrorDeRol] = useState<string | null>(null);

  const gente = useQuery({
    queryKey: ['membresias'],
    queryFn: () => pedir<Membresia[]>('/membresias'),
  });
  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: () => pedir<Rol[]>('/roles'),
    enabled: puedeVerRoles,
  });
  const permisos = useQuery({
    queryKey: ['permisos'],
    queryFn: () => pedir<Permiso[]>('/permisos'),
    // También hacen falta para corregir un rol, no sólo para crearlo: sin ellas el diálogo de
    // corrección se abre con la lista vacía y parece que el rol no tuviera ninguno.
    enabled: puedeCrearRoles || puedeEditarRoles,
  });
  const sucursales = useQuery({
    queryKey: ['sucursales'],
    queryFn: () => pedir<Sucursal[]>('/sucursales'),
    enabled: puedeEditarGente && puedeVerSucursales,
  });

  const invitar = useMutation({
    mutationFn: () =>
      pedir<{ id: string; token: string }>('/membresias/invitaciones', {
        method: 'POST',
        cuerpo: { correo, rolId: rolElegido },
      }),
    onSuccess: (respuesta) => {
      setEnlace(`${globalThis.location.origin}/invitacion?token=${respuesta.token}`);
      setErrorDeInvitacion(null);
      setCorreo('');
    },
    onError: (fallo: unknown) => {
      setErrorDeInvitacion(textoDeError(fallo));
    },
  });

  const crearRol = useMutation({
    mutationFn: () =>
      pedir<{ id: string }>('/roles', {
        method: 'POST',
        cuerpo: { ...rolNuevo, permisos: [...permisosElegidos] },
      }),
    onSuccess: async () => {
      setCreandoRol(false);
      setRolNuevo({ codigo: '', nombre: '' });
      setPermisosElegidos(new Set());
      setErrorDeRol(null);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['roles'] });
    },
    onError: (fallo: unknown) => {
      setErrorDeRol(textoDeError(fallo));
    },
  });

  const columnasDeGente: Columna<Membresia>[] = [
    {
      clave: 'persona',
      titulo: t('gente.persona'),
      contenido: (fila) => (
        <div>
          <div>{`${fila.nombre} ${fila.apellido ?? ''}`.trim()}</div>
          <div className="text-xs text-texto-tenue">{fila.correo}</div>
        </div>
      ),
    },
    { clave: 'rol', titulo: t('gente.rol'), contenido: (fila) => fila.rol },
    {
      clave: 'alcance',
      titulo: t('gente.alcance'),
      contenido: (fila) =>
        fila.sucursalesPermitidas === null ? (
          <span className="text-texto-tenue">{t('gente.todasLasSucursales')}</span>
        ) : (
          <span className="numero">{fila.sucursalesPermitidas.length}</span>
        ),
    },
    {
      clave: 'estado',
      titulo: t('gente.estado'),
      contenido: (fila) => (
        <Insignia tono={fila.activa ? 'bien' : 'neutro'}>
          {fila.activa ? t('gente.activa') : t('gente.inactiva')}
        </Insignia>
      ),
    },
    {
      clave: 'acciones',
      titulo: '',
      contenido: (fila) =>
        // El botón está en todas las filas, **también en la propia**: una fila sin botón se lee
        // como «esta pantalla no edita». Lo que no se puede sobre uno mismo se bloquea adentro de
        // la ficha, con el motivo a la vista.
        !puedeEditarGente ? null : (
          <div className="flex justify-end">
            <Boton
              onClick={() => {
                setCorrigiendo(fila);
              }}
            >
              {t('acciones.corregir')}
            </Boton>
          </div>
        ),
    },
  ];

  const columnasDeRoles: Columna<Rol>[] = [
    { clave: 'nombre', titulo: t('gente.rol'), contenido: (fila) => fila.nombre },
    {
      clave: 'tipo',
      titulo: t('gente.tipo'),
      contenido: (fila) => (fila.esDeSistema ? <Insignia>{t('gente.deSistema')}</Insignia> : null),
    },
    {
      clave: 'permisos',
      titulo: t('gente.permisos'),
      numerica: true,
      contenido: (fila) => fila.permisos.length,
    },
    {
      clave: 'personas',
      titulo: t('gente.personas'),
      numerica: true,
      contenido: (fila) => fila.personas,
    },
    {
      clave: 'acciones',
      titulo: '',
      contenido: (fila) => (
        <div className="flex justify-end gap-2">
          {puedeAdministrarTipos ? (
            <Boton
              onClick={() => {
                setEligiendoTipos(fila);
              }}
            >
              {t('gente.tiposDeComprobante')}
            </Boton>
          ) : null}
          {puedeEditarRoles ? (
            <Boton
              onClick={() => {
                setCorrigiendoRol(fila);
              }}
            >
              {t('acciones.corregir')}
            </Boton>
          ) : null}
        </div>
      ),
    },
  ];

  /**
   * El rol al que se le están eligiendo los tipos de comprobante.
   *
   * Va acá y no en la pantalla de tipos porque la pregunta es «qué puede hacer este rol», y ésa se
   * hace mirando los roles. En la otra pantalla se define qué **es** cada tipo.
   */
  const [eligiendoTipos, setEligiendoTipos] = useState<Rol | null>(null);

  const porModulo = new Map<string, Permiso[]>();
  for (const permiso of permisos.data ?? []) {
    porModulo.set(permiso.modulo, [...(porModulo.get(permiso.modulo) ?? []), permiso]);
  }

  return (
    <>
      <Encabezado
        titulo={t('gente.titulo')}
        descripcion={t('gente.descripcion')}
        acciones={
          puedeInvitar ? (
            <Boton
              tono="principal"
              onClick={() => {
                setInvitando(true);
                setEnlace(null);
              }}
            >
              {t('gente.invitar')}
            </Boton>
          ) : undefined
        }
      />

      <div className="flex flex-col gap-4">
        <Tarjeta titulo={t('gente.equipo')}>
          {gente.isPending ? <Cargando /> : null}
          {gente.isError ? (
            <Problema
              error={gente.error}
              reintentar={() => {
                void gente.refetch();
              }}
            />
          ) : null}
          {gente.isSuccess ? (
            <Tabla
              columnas={columnasDeGente}
              filas={gente.data}
              claveDeFila={(fila) => fila.id}
              vacio={<Vacio />}
            />
          ) : null}
        </Tarjeta>

        {puedeVerRoles ? (
          <Tarjeta
            titulo={t('gente.roles')}
            descripcion={t('gente.ayudaRoles')}
            acciones={
              puedeCrearRoles ? (
                <Boton
                  onClick={() => {
                    setCreandoRol(true);
                  }}
                >
                  {t('acciones.agregar')}
                </Boton>
              ) : undefined
            }
          >
            {roles.isPending ? <Cargando /> : null}
            {roles.isSuccess ? (
              <Tabla
                columnas={columnasDeRoles}
                filas={roles.data}
                claveDeFila={(fila) => fila.id}
                vacio={<Vacio />}
              />
            ) : null}
          </Tarjeta>
        ) : null}
      </div>

      <Dialogo
        abierto={invitando}
        titulo={t('gente.invitar')}
        descripcion={t('gente.ayudaInvitar')}
        onCerrar={() => {
          setInvitando(false);
          setEnlace(null);
          setErrorDeInvitacion(null);
        }}
        acciones={
          enlace === null ? (
            <Boton
              tono="principal"
              cargando={invitar.isPending}
              onClick={() => {
                invitar.mutate();
              }}
            >
              {t('gente.enviarInvitacion')}
            </Boton>
          ) : undefined
        }
      >
        {enlace === null ? (
          <div className="flex flex-col gap-3">
            <Campo
              etiqueta={t('gente.correo')}
              type="email"
              obligatorio
              value={correo}
              onChange={(evento) => {
                setCorreo(evento.target.value);
              }}
            />
            <Selector
              etiqueta={t('gente.rol')}
              obligatorio
              value={rolElegido}
              onChange={(evento) => {
                setRolElegido(evento.target.value);
              }}
            >
              <option value="" disabled>
                {t('gente.elegirRol')}
              </option>
              {(roles.data ?? []).map((rol) => (
                <option key={rol.id} value={rol.id}>
                  {rol.nombre}
                </option>
              ))}
            </Selector>
            {errorDeInvitacion === null ? null : (
              <p role="alert" className="text-xs text-mal">
                {errorDeInvitacion}
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <p className="text-sm">{t('gente.invitacionLista')}</p>
            {/* El enlace se muestra para poder pasarlo por donde sea, sin depender de que
                el correo haya salido. */}
            <code className="break-all rounded-marco border border-borde bg-lienzo p-2 text-xs">
              {enlace}
            </code>
          </div>
        )}
      </Dialogo>

      <Dialogo
        abierto={creandoRol}
        titulo={t('gente.nuevoRol')}
        descripcion={t('gente.ayudaNuevoRol')}
        onCerrar={() => {
          setCreandoRol(false);
          setErrorDeRol(null);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={crearRol.isPending}
            onClick={() => {
              crearRol.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <Campo
            etiqueta={t('gente.codigoDelRol')}
            obligatorio
            value={rolNuevo.codigo}
            onChange={(evento) => {
              setRolNuevo((previo) => ({
                ...previo,
                codigo: evento.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''),
              }));
            }}
          />
          <Campo
            etiqueta={t('gente.nombreDelRol')}
            obligatorio
            value={rolNuevo.nombre}
            onChange={(evento) => {
              setRolNuevo((previo) => ({ ...previo, nombre: evento.target.value }));
            }}
          />

          <ArbolDePermisos
            porModulo={porModulo}
            elegidos={permisosElegidos}
            onCambiar={setPermisosElegidos}
          />

          {errorDeRol === null ? null : (
            <p role="alert" className="text-xs text-mal">
              {errorDeRol}
            </p>
          )}
        </div>
      </Dialogo>
      {corrigiendo === null ? null : (
        <FichaDePersona
          membresia={corrigiendo}
          esUnoMismo={corrigiendo.usuarioId === perfil?.id}
          roles={roles.data ?? []}
          sucursales={(sucursales.data ?? []).filter((sucursal) => sucursal.activa)}
          onCerrar={() => {
            setCorrigiendo(null);
          }}
        />
      )}
      {corrigiendoRol === null ? null : (
        <FichaDeRol
          rol={corrigiendoRol}
          porModulo={porModulo}
          onCerrar={() => {
            setCorrigiendoRol(null);
          }}
        />
      )}
      {eligiendoTipos === null ? null : (
        <TiposDelRol
          rol={eligiendoTipos}
          onCerrar={() => {
            setEligiendoTipos(null);
          }}
        />
      )}
    </>
  );
}

/**
 * Qué tipos de comprobante puede usar un rol.
 *
 * **Sin marcar ninguno, puede usar todos**, y la pantalla lo dice con todas las letras: es la
 * diferencia entre «todavía no lo restringí» y «no puede emitir nada», y confundirlas deja a un
 * cajero sin poder vender sin que nadie entienda por qué.
 */
function TiposDelRol({
  rol,
  onCerrar,
}: {
  readonly rol: Rol;
  readonly onCerrar: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const cola = useQueryClient();
  const textoDeError = useTextoDeError();
  const [error, setError] = useState<string | null>(null);

  const tipos = useQuery({
    queryKey: ['tipos-de-comprobante'],
    queryFn: () =>
      pedir<{ id: string; nombre: string; activo: boolean }[]>('/tipos-de-comprobante'),
  });
  const suyos = useQuery({
    queryKey: ['tipos-del-rol', rol.id],
    queryFn: () => pedir<string[]>(`/tipos-de-comprobante/del-rol/${rol.id}`),
  });

  const guardar = useMutation({
    mutationFn: (elegidos: readonly string[]) =>
      pedir<void>(`/tipos-de-comprobante/del-rol/${rol.id}`, {
        method: 'POST',
        cuerpo: { tipos: elegidos },
      }),
    onSuccess: async () => {
      setError(null);
      await cola.invalidateQueries({ queryKey: ['tipos-del-rol', rol.id] });
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  const elegidos = suyos.data ?? [];

  return (
    <Dialogo
      abierto
      titulo={t('gente.tiposDeComprobante')}
      descripcion={rol.nombre}
      onCerrar={onCerrar}
    >
      <div className="flex flex-col gap-2">
        <p className="text-apoyo text-texto-tenue">{t('tiposDeComprobante.todosLosTipos')}</p>
        {(tipos.data ?? [])
          .filter((tipo) => tipo.activo)
          .map((tipo) => (
            <Interruptor
              key={tipo.id}
              etiqueta={tipo.nombre}
              checked={elegidos.includes(tipo.id)}
              onChange={(evento) => {
                guardar.mutate(
                  evento.target.checked
                    ? [...elegidos, tipo.id]
                    : elegidos.filter((id) => id !== tipo.id),
                );
              }}
            />
          ))}
        {error === null ? null : (
          <p role="alert" className="text-apoyo text-mal">
            {error}
          </p>
        )}
      </div>
    </Dialogo>
  );
}

/**
 * Los permisos, agrupados por módulo.
 *
 * Vive una sola vez porque son la misma lista al crear un rol y al corregirlo, y dos copias de una
 * lista de permisos se separan a la primera vez que se agrega un módulo.
 */
function ArbolDePermisos({
  porModulo,
  elegidos,
  onCambiar,
  bloqueado,
}: {
  readonly porModulo: ReadonlyMap<string, Permiso[]>;
  readonly elegidos: ReadonlySet<string>;
  readonly onCambiar: (calcular: (previos: Set<string>) => Set<string>) => void;
  readonly bloqueado?: boolean;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="text-xs font-medium text-texto-tenue">{t('gente.permisos')}</legend>
      <div className="max-h-64 overflow-y-auto rounded-marco border border-borde p-2">
        {[...porModulo.entries()].map(([modulo, delModulo]) => (
          <div key={modulo} className="mb-2">
            <p className="mb-1 text-xs font-medium">
              {t(`modulos.${modulo}`, { defaultValue: modulo })}
            </p>
            {delModulo.map((permiso) => (
              <label key={permiso.codigo} className="flex items-center gap-2 py-0.5 text-sm">
                <input
                  type="checkbox"
                  checked={elegidos.has(permiso.codigo)}
                  disabled={bloqueado === true}
                  onChange={(evento) => {
                    onCambiar((previos) => {
                      const nuevos = new Set(previos);
                      if (evento.target.checked) nuevos.add(permiso.codigo);
                      else nuevos.delete(permiso.codigo);
                      return nuevos;
                    });
                  }}
                />
                <span>{t(permiso.claveDeTraduccion, { defaultValue: permiso.codigo })}</span>
              </label>
            ))}
          </div>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * Corregir a una persona del comercio.
 *
 * ## Qué se corrige y qué no
 *
 * El rol, hasta dónde llega y si sigue trabajando acá. **El nombre y el correo no**: son de la
 * persona, no de esta empresa, y la misma cuenta puede estar en varias.
 *
 * ## Por qué la baja es un interruptor y no un botón de borrar
 *
 * Porque una membresía no se borra nunca: lo que esa persona vendió, cobró y asentó sigue a su
 * nombre. Desactivarla es sacarle la entrada, no sacarla de la historia (regla 7).
 *
 * ## Sobre uno mismo
 *
 * La ficha se abre igual —una fila sin botón se lee como «esta pantalla no edita»— pero el rol y la
 * baja quedan bloqueados **con el motivo a la vista**: el servidor cuida que quede un dueño, y
 * quedarse afuera de la propia empresa por un clic no se arregla desde adentro. El alcance sí se
 * toca: acotarse una sucursal no deja a nadie sin poder entrar.
 */
function FichaDePersona({
  membresia,
  esUnoMismo,
  roles,
  sucursales,
  onCerrar,
}: {
  readonly membresia: Membresia;
  readonly esUnoMismo: boolean;
  readonly roles: readonly Rol[];
  readonly sucursales: readonly Sucursal[];
  readonly onCerrar: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const cola = useQueryClient();
  const textoDeError = useTextoDeError();

  const [rolId, setRolId] = useState(membresia.rolId);
  const [activa, setActiva] = useState(membresia.activa);
  const [acotada, setAcotada] = useState(membresia.sucursalesPermitidas !== null);
  const [permitidas, setPermitidas] = useState<Set<string>>(
    new Set(membresia.sucursalesPermitidas ?? []),
  );
  const [predeterminada, setPredeterminada] = useState(membresia.sucursalPredeterminadaId ?? '');
  const [error, setError] = useState<string | null>(null);

  // **Sin poder ver las sucursales no se toca el alcance.** Quien administra gente puede no tener
  // permiso para ver la estructura; entonces la lista llega vacía, y dibujar el interruptor sobre
  // una lista vacía deja tocarlo hasta dejar a alguien con «ninguna sucursal» sin ver cuáles eran.
  // No se ofrece, y tampoco se manda: el alcance queda como estaba.
  const seTocaElAlcance = sucursales.length > 0;

  // La sucursal de siempre sale de las permitidas, no de todas: ofrecer una que la persona no
  // puede ver la dejaría entrando todos los días a una pantalla que le va a decir que no.
  const disponibles = acotada
    ? sucursales.filter((sucursal) => permitidas.has(sucursal.id))
    : sucursales;
  const predeterminadaEfectiva = disponibles.some((sucursal) => sucursal.id === predeterminada)
    ? predeterminada
    : '';

  const guardar = useMutation({
    mutationFn: () =>
      pedir<void>(`/membresias/${membresia.id}`, {
        method: 'PATCH',
        cuerpo: {
          rolId,
          activa,
          ...(seTocaElAlcance
            ? {
                sucursalesPermitidas: acotada ? [...permitidas] : null,
                sucursalPredeterminadaId:
                  predeterminadaEfectiva === '' ? null : predeterminadaEfectiva,
              }
            : {}),
        },
      }),
    onSuccess: async () => {
      setError(null);
      await cola.invalidateQueries({ queryKey: ['membresias'] });
      onCerrar();
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  return (
    <Dialogo
      abierto
      titulo={t('gente.corregirPersona')}
      descripcion={membresia.correo}
      onCerrar={onCerrar}
      acciones={
        <Boton
          tono="principal"
          cargando={guardar.isPending}
          onClick={() => {
            guardar.mutate();
          }}
        >
          {t('acciones.guardar')}
        </Boton>
      }
    >
      <div className="flex flex-col gap-3">
        {esUnoMismo ? <p className="text-xs text-texto-tenue">{t('gente.sobreUnoMismo')}</p> : null}

        <Selector
          etiqueta={t('gente.rol')}
          value={rolId}
          disabled={esUnoMismo}
          onChange={(evento) => {
            setRolId(evento.target.value);
          }}
        >
          {roles.map((rol) => (
            <option key={rol.id} value={rol.id}>
              {rol.nombre}
            </option>
          ))}
        </Selector>

        <Interruptor
          etiqueta={t('gente.estadoDeLaMembresia')}
          ayuda={t('gente.ayudaEstadoDeLaMembresia')}
          disabled={esUnoMismo}
          checked={activa}
          onChange={(evento) => {
            setActiva(evento.target.checked);
          }}
        />

        {!seTocaElAlcance ? null : (
          <>
            <Interruptor
              // La celda de la tabla dice «Todas» porque su columna ya dice de qué; acá el interruptor
              // está suelto entre otros y tiene que decir de qué es.
              etiqueta={t('gente.sinAcotarSucursales')}
              ayuda={t('gente.ayudaTodasLasSucursales')}
              checked={!acotada}
              onChange={(evento) => {
                setAcotada(!evento.target.checked);
              }}
            />

            {!acotada ? null : (
              <fieldset className="flex flex-col gap-1 rounded-marco border border-borde p-2">
                <legend className="text-xs font-medium text-texto-tenue">
                  {t('gente.alcance')}
                </legend>
                {sucursales.map((sucursal) => (
                  <label key={sucursal.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={permitidas.has(sucursal.id)}
                      onChange={(evento) => {
                        setPermitidas((previas) => {
                          const nuevas = new Set(previas);
                          if (evento.target.checked) nuevas.add(sucursal.id);
                          else nuevas.delete(sucursal.id);
                          return nuevas;
                        });
                      }}
                    />
                    <span>{sucursal.nombre}</span>
                  </label>
                ))}
              </fieldset>
            )}

            <Selector
              etiqueta={t('gente.sucursalPredeterminada')}
              ayuda={t('gente.ayudaSucursalPredeterminada')}
              value={predeterminadaEfectiva}
              onChange={(evento) => {
                setPredeterminada(evento.target.value);
              }}
            >
              <option value="">{t('gente.sinPredeterminada')}</option>
              {disponibles.map((sucursal) => (
                <option key={sucursal.id} value={sucursal.id}>
                  {sucursal.nombre}
                </option>
              ))}
            </Selector>
          </>
        )}

        {error === null ? null : (
          <p role="alert" className="text-xs text-mal">
            {error}
          </p>
        )}
      </div>
    </Dialogo>
  );
}

/**
 * Corregir un rol: su nombre y qué puede hacer.
 *
 * ## Lo que la pantalla no deja
 *
 * **Cambiarle el código.** Es el nombre con el que lo buscan las reglas —el de dueño, sin ir más
 * lejos— y renombrarlo convertiría un rol conocido en uno cualquiera.
 *
 * **Sacarle permisos al dueño.** El servidor lo rechaza y acá se ve el motivo en vez del error:
 * una empresa sin dueño no se arregla desde adentro, porque no queda nadie que pueda devolver
 * permisos.
 */
function FichaDeRol({
  rol,
  porModulo,
  onCerrar,
}: {
  readonly rol: Rol;
  readonly porModulo: ReadonlyMap<string, Permiso[]>;
  readonly onCerrar: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const cola = useQueryClient();
  const textoDeError = useTextoDeError();
  const puedeEliminar = usePermiso('ACCESOS.ROL.ELIMINAR');

  const esElDuenio = rol.codigo === 'DUENIO';
  const [nombre, setNombre] = useState(rol.nombre);
  const [elegidos, setElegidos] = useState<Set<string>>(new Set(rol.permisos));
  const [confirmando, setConfirmando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refrescar = async (): Promise<void> => {
    setError(null);
    await cola.invalidateQueries({ queryKey: ['roles'] });
    onCerrar();
  };
  const alFallar = (fallo: unknown): void => {
    setError(textoDeError(fallo));
  };

  const guardar = useMutation({
    mutationFn: () =>
      pedir<void>(`/roles/${rol.id}`, {
        method: 'PATCH',
        // Al dueño sólo se le manda el nombre: mandarle los permisos —aunque sean los mismos— es
        // lo que el servidor rechaza, y con razón.
        cuerpo: esElDuenio
          ? { nombre: nombre.trim() }
          : { nombre: nombre.trim(), permisos: [...elegidos] },
      }),
    onSuccess: refrescar,
    onError: alFallar,
  });

  const eliminar = useMutation({
    mutationFn: () => pedir<void>(`/roles/${rol.id}`, { method: 'DELETE' }),
    onSuccess: refrescar,
    onError: alFallar,
  });

  return (
    <Dialogo
      abierto
      titulo={t('gente.corregirRol')}
      descripcion={t('gente.ayudaCorregirRol')}
      onCerrar={onCerrar}
      acciones={
        <Boton
          tono="principal"
          cargando={guardar.isPending}
          onClick={() => {
            guardar.mutate();
          }}
        >
          {t('acciones.guardar')}
        </Boton>
      }
    >
      <div className="flex flex-col gap-3">
        <Campo
          etiqueta={t('gente.nombreDelRol')}
          obligatorio
          value={nombre}
          onChange={(evento) => {
            setNombre(evento.target.value);
          }}
        />

        {esElDuenio ? (
          <p className="text-xs text-texto-tenue">{t('gente.permisosDelDuenio')}</p>
        ) : null}
        <ArbolDePermisos
          porModulo={porModulo}
          elegidos={elegidos}
          onCambiar={setElegidos}
          bloqueado={esElDuenio}
        />

        {!puedeEliminar ? null : rol.esDeSistema ? (
          <p className="text-xs text-texto-tenue">{t('gente.rolDeSistemaNoSeElimina')}</p>
        ) : confirmando ? (
          <div className="flex flex-col gap-2 rounded-marco border border-borde p-2">
            <p className="text-xs">{t('gente.confirmarEliminarRol')}</p>
            <div className="flex gap-2">
              <Boton
                tono="peligro"
                cargando={eliminar.isPending}
                onClick={() => {
                  eliminar.mutate();
                }}
              >
                {t('gente.eliminarRol')}
              </Boton>
              <Boton
                tono="secundario"
                onClick={() => {
                  setConfirmando(false);
                }}
              >
                {t('acciones.cancelar')}
              </Boton>
            </div>
          </div>
        ) : (
          <div className="flex">
            <Boton
              tono="sutil"
              onClick={() => {
                setConfirmando(true);
              }}
            >
              {t('gente.eliminarRol')}
            </Boton>
          </div>
        )}

        {error === null ? null : (
          <p role="alert" className="text-xs text-mal">
            {error}
          </p>
        )}
      </div>
    </Dialogo>
  );
}
